import { DSButtonV2 } from '@elliemae/ds-button';
import {
  CONTROLLED_DATE_TIME_PICKER_TYPES,
  DSControlledDateTimePicker,
  getDayFromDateString,
} from '@elliemae/ds-controlled-form';
import {
  CHECKBOX_VARIANT,
  DSCheckbox,
  DSInputMask,
  DSLargeInputText,
  DSTextBox,
  MASK_TYPES,
} from '@elliemae/ds-form';
import { DSFormLayoutBlockItem } from '@elliemae/ds-form-layout-blocks';
import { Grid } from '@elliemae/ds-grid';
import { Delete } from '@elliemae/ds-icons';
import { DSModal, MODAL_SUB_TYPE_V2, MODAL_TYPE_V2 } from '@elliemae/ds-modal';
import { DSSeparator } from '@elliemae/ds-separator';
import { bool, func, number, oneOfType, shape, string } from 'prop-types';
import { useState } from 'react';
import { formatDate } from '@frontend/utils';
import { DSSingleComboBox } from '@elliemae/ds-form-single-combobox';
import { CheckBoxWrapper } from '../../WrapContainer/styled';
import { parseDecimals } from './helpers';
import { StyledCard, StyledCardTitle, StyledTextTitle } from './styled';
import { useIsZeroBasedPricing } from '../../../../common/utils/customHooks/useIsZeroBasedPricing';
import { useDescription } from '../../../../common/utils/customHooks/useDescription';

export const ConcessionCard = ({
  rowCard,
  onChange,
  onDeleteRow,
  index,
  title,
  errors,
}: {
  rowCard: any;
  onChange: any;
  onDeleteRow: any;
  index: number;
  title: any;
  errors: any;
}) => {
  const {
    paidByOptions,
    adjustmentReasonsOptions,
    selectedPaidByValue,
    setSelectedPaidByValue,
    selectedAdjustmentReasonValue,
    setSelectedAdjustmentReasonValue,
    rowCard: updatedRowCard,
  } = useDescription(rowCard);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTime = today.getTime();
  const approvalDateValidationMessage = 'Cannot select past date';
  const [openModal, setOpenModal] = useState(false);
  const [error, setError] = useState(false);

  const getApprovalDate = () => {
    if (updatedRowCard?.approvalDate?.includes('T')) {
      return formatDate(updatedRowCard.approvalDate, 'Calendar');
    }
    return updatedRowCard?.approvalDate ?? '';
  };
  const getIsBeforeToday = (dayString) => {
    const day = getDayFromDateString(dayString);
    if (day) {
      return day.getTime() > todayTime;
    }
    return false;
  };
  const isZeroBasedPricing = useIsZeroBasedPricing();

  return (
    <>
      <StyledCard>
        <StyledCardTitle>
          <StyledTextTitle>{`${title} ${index + 1}`}</StyledTextTitle>
          <DSButtonV2
            buttonType="icon"
            onClick={() => setOpenModal(true)}
            size="s"
            aria-label="Delete row"
          >
            <Delete size="s" color={['brand-primary', 600]} />
          </DSButtonV2>
        </StyledCardTitle>
        <Grid
          cols={['20%', '40%', '40%']}
          py={10}
          gutter={5}
          alignItems="flex-start"
        >
          <DSFormLayoutBlockItem
            label="Price"
            inputID="priceConcession"
            required
            hasError={Boolean(errors?.price)}
            validationMessage={errors?.price?.toString()}
            withHighlight={Boolean(errors?.price)}
            feedbackMessage={`Par pricing is ${
              isZeroBasedPricing ? `0.00` : `100.00`
            }`}
          >
            <DSInputMask
              mask={(num) =>
                MASK_TYPES.NUMBER({
                  prefix: '',
                  integerLimit: 1,
                  allowDecimal: true,
                  decimalLimit: 3,
                  allowNegative: true,
                })(num)
              }
              name="price"
              value={updatedRowCard.price}
              labelText="Price"
              required
              onChange={({ target: { value } }) => {
                onChange(value, 'price', index);
              }}
              onBlur={({ target: { value } }) => {
                onChange(parseDecimals(value, 3), 'price', index);
              }}
              readOnly={updatedRowCard.status === 'Submitted'}
            />
          </DSFormLayoutBlockItem>
          <DSFormLayoutBlockItem
            label="Approval Date"
            inputID="approvalDateConcession"
            hasError={error}
            validationMessage={approvalDateValidationMessage}
            required
            fitContent
          >
            <DSControlledDateTimePicker
              date={getApprovalDate()}
              getIsDisabledDay={getIsBeforeToday}
              onDateChange={(newVal) => {
                onChange(newVal, 'approvalDate', index);
                setError(getIsBeforeToday(newVal));
              }}
              type={CONTROLLED_DATE_TIME_PICKER_TYPES.DATE.FULL}
              disabled={updatedRowCard.status === 'Submitted'}
              hasError={error}
              pickerFooterMsg={error ? approvalDateValidationMessage : ''}
            />
          </DSFormLayoutBlockItem>
          <DSFormLayoutBlockItem
            label="Approved By"
            inputID="approvedByConcession"
            // feedbackMessage="Feedback message"
            // validationMessage="Validation Message too long is being shorted"
          >
            <DSTextBox
              value={updatedRowCard.approvedBy}
              onChange={({ target: { value } }) =>
                onChange(value, 'approvedBy', index)
              }
              maxLength={256}
              readOnly={updatedRowCard.status === 'Submitted'}
            />
          </DSFormLayoutBlockItem>
        </Grid>
        <Grid cols={['100%', '0']}>
          <DSFormLayoutBlockItem
            label="Approval Reason"
            inputID="approvedReason"
            hasError={Boolean(errors?.approvedReason)}
            validationMessage={errors?.approvedReason?.toString()}
            withHighlight={Boolean(errors?.approvedReason)}
            // feedbackMessage="Feedback message"
            // validationMessage="Validation Message too long is being shorted"
          >
            <DSLargeInputText
              value={updatedRowCard.approvedReason}
              onChange={({ target: { value } }) =>
                onChange(value, 'approvedReason', index)
              }
              maxLength={2000}
              disabled={updatedRowCard.status === 'Submitted'}
            />
          </DSFormLayoutBlockItem>
          <CheckBoxWrapper mt="14px">
            <DSCheckbox
              labelText="Persist during price updates"
              checked
              variant={CHECKBOX_VARIANT.DEFAULT}
              name="persistPrice"
              id="persistPrice"
              containerProps={{
                style: { visibility: 'hidden' },
              }}
            />
          </CheckBoxWrapper>
        </Grid>
        <Grid cols={['1fr', '1fr']} gutter={5} alignItems="flex-start">
          {paidByOptions?.length ? (
            <DSFormLayoutBlockItem
              mb="xs"
              label="Paid By"
              inputID="adjustment_paid_by"
              hasError={![undefined, null, ''].includes(errors?.paidByGuid)}
              validationMessage={errors?.paidByGuid}
              withHighlight={Boolean(errors?.paidByGuid)}
              required
            >
              <DSSingleComboBox
                placeholder="Select the paid by category"
                allOptions={paidByOptions}
                onChange={(e) => {
                  const value = e?.value === null ? undefined : e?.value;
                  onChange(value, 'paidByGuid', index);
                  setSelectedPaidByValue(e ?? {});
                }}
                selectedValue={selectedPaidByValue}
                id="adjustment_paid_by"
              />
            </DSFormLayoutBlockItem>
          ) : (
            <div></div>
          )}
          {adjustmentReasonsOptions?.length ? (
            <DSFormLayoutBlockItem
              mb="xs"
              label="Adjustment Reason"
              inputID="adjustment_reason"
              hasError={
                ![undefined, null, ''].includes(errors?.adjustmentReasonGuid)
              }
              validationMessage={errors?.adjustmentReasonGuid}
              withHighlight={Boolean(errors?.adjustmentReasonGuid)}
              required
            >
              <DSSingleComboBox
                placeholder="Select the adjustment reason"
                allOptions={adjustmentReasonsOptions}
                onChange={(e) => {
                  const value = e?.value === null ? undefined : e?.value;
                  onChange(value, 'adjustmentReasonGuid', index);
                  setSelectedAdjustmentReasonValue(e ?? {});
                }}
                selectedValue={selectedAdjustmentReasonValue}
                id="adjustment_paid_by"
              />
            </DSFormLayoutBlockItem>
          ) : (
            <div></div>
          )}
        </Grid>
        <DSSeparator dashed style={{ marginBottom: `0`, marginTop: `24px` }} />
      </StyledCard>
      <DSModal
        centered
        confirmLabel="Ok"
        isOpen={openModal}
        modalSubType={MODAL_SUB_TYPE_V2.WARNING}
        modalType={MODAL_TYPE_V2.DECISION}
        onReject={() => setOpenModal(false)}
        onClose={() => setOpenModal(false)}
        onConfirm={() => {
          onDeleteRow(index);
          setOpenModal(false);
        }}
        rejectLabel="Cancel"
        showClose
        showRejectButton
        version={2}
      >
        <p>Do you want to delete this entry?</p>
      </DSModal>
    </>
  );
};

ConcessionCard.defaultProps = {
  index: 0,
  rowCard: {
    title: '',
    approvalDate: '',
    price: '',
    approvedBy: '',
    approvedReason: '',
    persist: true,
    persistDisabled: false,
  },
  title: '',
  errors: {
    hasError: false,
    messages: [''],
  },
};

ConcessionCard.propTypes = {
  onChange: func.isRequired,
  onDeleteRow: func.isRequired,
  index: number,
  rowCard: shape({
    name: string,
    price: oneOfType([number, string]),
    persist: bool,
    persistDisabled: bool,
  }),
  title: string,
  errors: shape({
    hasError: false as any,
    messages: [] as any,
  }),
};
